import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../../api.js';
import { useI18n, usePageTitle } from '../../i18n/index.jsx';
import { useLive } from '../../live.jsx';
import { categoryName } from '../../format.js';
import { Alert } from '../../components/ui.jsx';
import { openPrivateFile } from '../../privateFile.js';
import { AccountHead, useAccount } from '../account/AccountLayout.jsx';
import { flag, formatQty, money, shortDate, destination, timeframeText, inDays } from './requestUtils.js';
import './requests.css';

// My space › one of my buying requests: its status and actions (edit,
// close, fulfilled, extend, delete) and the offers received — side by side
// ("Compare") or as cards — to shortlist, reply, accept or decline.
export default function MyRequestDetail() {
  const { id } = useParams();
  const { t, lang, errorText } = useI18n();
  const navigate = useNavigate();
  const { reload } = useAccount();
  usePageTitle('buying.myTitle');
  const [r, setR] = useState(null);
  const [offers, setOffers] = useState(null);
  const [waiting, setWaiting] = useState(0);
  const [view, setView] = useState(() => (typeof window !== 'undefined' && window.innerWidth < 700 ? 'list' : 'compare'));
  const [accepting, setAccepting] = useState(null);
  const [extendTo, setExtendTo] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(() => {
    api(`/my/buying-requests/${id}`).then(setR).catch((e) => setError(errorText(e)));
    api(`/my/buying-requests/${id}/offers`).then((d) => { setOffers(d.offers); setWaiting(d.waitingAtPme); }).catch(() => setOffers([]));
  }, [id, errorText]);
  useEffect(() => { load(); }, [load]);
  useLive(['requests'], () => { load(); });

  async function act(path, body, confirmText) {
    if (confirmText && !window.confirm(confirmText)) return;
    setError('');
    try { setR(await api(`/my/buying-requests/${id}/${path}`, { method: 'POST', body: body || {} })); reload(); } catch (e) { setError(errorText(e)); }
  }
  async function remove() {
    if (!window.confirm(t('buying.actions.deleteConfirm'))) return;
    try { await api(`/my/buying-requests/${id}`, { method: 'DELETE' }); navigate('/account/buying-requests'); } catch (e) { setError(errorText(e)); }
  }
  async function setStatus(o, status, keepOpen = false) {
    setError('');
    try {
      const d = await api(`/my/buying-requests/${id}/offers/${o.id}/status`, { method: 'POST', body: { status, keepOpen } });
      setOffers((list) => list.map((x) => (x.id === o.id ? d.offer : x)));
      setR(d.request); setAccepting(null); reload();
    } catch (e) { setError(errorText(e)); }
  }

  if (!r) return <div className="acc-page"><Alert>{error}</Alert>{!error && <p className="muted">{t('common.loading')}</p>}</div>;

  const ended = ['fulfilled', 'closed', 'expired'].includes(r.status);
  const live = offers?.filter((o) => o.status !== 'withdrawn') || [];
  const best = live.length > 1 ? Math.min(...live.map((o) => o.price ?? Infinity)) : null;
  const chatLink = (o) => (r.anonymous ? '/account/messages' : o.conversationId ? `/account/messages/${o.conversationId}` : '/account/messages');
  const sellerCell = (o) => (
    <span className="seller-cell">
      {o.seller?.logoUrl ? <img src={o.seller.logoUrl} alt="" /> : <span className="offer-logo" aria-hidden="true" />}
      <span><strong>{o.seller?.companyName}</strong>{o.seller?.country && <><br /><span className="small muted">{flag(o.seller.country)} {o.seller.country}</span></>}</span>
    </span>
  );
  const actions = (o) => (
    <span className="req-actions">
      {o.status !== 'accepted' && !ended && <button type="button" className="btn btn-primary btn-sm" onClick={() => setAccepting(o)}>{t('buying.offers.accept')}</button>}
      {o.status !== 'shortlisted' && o.status !== 'accepted' && <button type="button" className="btn btn-ghost btn-sm" onClick={() => setStatus(o, 'shortlisted')}>☆ {t('buying.offers.shortlist')}</button>}
      {o.status === 'shortlisted' && <button type="button" className="btn btn-ghost btn-sm" onClick={() => setStatus(o, 'new')}>★ {t('buying.offers.unshortlist')}</button>}
      <Link className="btn btn-ghost btn-sm" to={chatLink(o)}>{t('buying.offers.reply')}</Link>
      {o.status !== 'declined' && o.status !== 'accepted' && <button type="button" className="btn btn-link btn-sm" onClick={() => setStatus(o, 'declined')}>{t('buying.offers.decline')}</button>}
    </span>
  );
  const extras = (o) => (
    <>
      {o.listing && <Link to={`/marketplace/${o.listing.id}`} target="_blank">🛍 {o.listing.title}</Link>}
      {o.document && <><br /><a href={o.document.url} target="_blank" rel="noreferrer">🧾 {o.document.ref}</a></>}
      {o.files.map((f) => <React.Fragment key={f.id}><br /><a href={f.url} target="_blank" rel="noreferrer" onClick={(e) => { e.preventDefault(); openPrivateFile(f.url.replace(/^\/api/, '')).catch(() => {}); }}>📎 {f.name}</a></React.Fragment>)}
    </>
  );
  const rows = [
    ['seller', (o) => sellerCell(o)],
    ['price', (o) => <strong>{money(o.price, o.currency, lang)}</strong>, (o) => best !== null && o.price === best],
    ['quantity', (o) => formatQty(o.quantity, o.unit, lang, t)],
    ['total', (o) => (o.price !== null && o.quantity !== null ? money(o.price * o.quantity, o.currency, lang) : '—')],
    ['incoterm', (o) => o.incoterm || '—'],
    ['port', (o) => o.port || '—'],
    ['delivery', (o) => o.deliveryTime || '—'],
    ['validUntil', (o) => (o.validUntil ? shortDate(o.validUntil, lang) : '—')],
    ['message', (o) => <span className="offer-msg">{o.message || '—'}</span>],
    ['files', (o) => (o.listing || o.document || o.files.length ? extras(o) : '—')],
    ['status', (o) => <span className={`chip-status offer-status-${o.status}`}>{t(`buying.offerStatus.${o.status}`)}</span>],
  ];

  return (
    <div className="acc-page">
      <p><Link to="/account/buying-requests" className="back-link">← {t('buying.myTitle')}</Link></p>
      <AccountHead title={r.original?.title || r.title}
        lead={<><span className={`chip-status req-status-${r.status}`}>{t(`buying.status.${r.status}`)}</span> {categoryName(r.category, lang)} · {formatQty(r.quantity, r.unit, lang, t)}{destination(r, lang) ? ` · ${destination(r, lang)}` : ''}{timeframeText(r, lang) ? ` · ${timeframeText(r, lang)}` : ''} · {t(ended ? 'buying.expired' : 'buying.expires', { date: shortDate(r.expiresOn, lang) })}{r.anonymous ? ` · 🔒 ${t('buying.admin.anonymous')}` : ''}</>} />
      <Alert>{error}</Alert>
      {r.status === 'rejected' && <p className="callout callout-warning">{t('buying.detail.rejectedNote', { reason: r.rejectionReason })}</p>}
      {r.status === 'pending' && <p className="callout callout-info">{t('buying.detail.pendingNote')}</p>}

      <div className="req-actions" style={{ marginBottom: 16 }}>
        {r.publishedAt && <Link className="btn btn-ghost btn-sm" to={`/marketplace/requests/${r.id}`}>{t('buying.actions.view')}</Link>}
        {!ended && <Link className="btn btn-ghost btn-sm" to={`/account/buying-requests/${r.id}/edit`}>{t('buying.actions.edit')}</Link>}
        {!ended && r.status !== 'rejected' && <button type="button" className="btn btn-ghost btn-sm" onClick={() => act('close', { fulfilled: true })}>{t('buying.actions.fulfilled')}</button>}
        {!ended && r.status !== 'rejected' && <button type="button" className="btn btn-ghost btn-sm" onClick={() => act('close', {}, t('buying.actions.closeConfirm'))}>{t('buying.actions.close')}</button>}
        {['open', 'in_discussion', 'pending', 'expired'].includes(r.status) && (
          <span className="req-actions">
            <label className="sr-only" htmlFor="rq-ext">{t('buying.actions.extendTo')}</label>
            <input id="rq-ext" type="date" min={inDays(0)} max={inDays(180)} value={extendTo} onChange={(e) => setExtendTo(e.target.value)} style={{ width: 'auto' }} />
            <button type="button" className="btn btn-ghost btn-sm" disabled={!extendTo} onClick={() => act('extend', { expiresOn: extendTo })}>{t('buying.actions.extend')}</button>
          </span>
        )}
        {offers && offers.length === 0 && <button type="button" className="btn btn-link btn-sm" onClick={remove}>{t('buying.actions.delete')}</button>}
      </div>

      <section>
        <div className="req-section-head">
          <h2>{t('buying.offers.title')} {live.length > 0 && <span className="muted">({live.length})</span>}</h2>
          {live.length > 1 && (
            <span className="view-toggle" role="group">
              <button type="button" className={view === 'compare' ? 'on' : ''} aria-pressed={view === 'compare'} onClick={() => setView('compare')}>{t('buying.offers.compare')}</button>
              <button type="button" className={view === 'list' ? 'on' : ''} aria-pressed={view === 'list'} onClick={() => setView('list')}>{t('buying.offers.list')}</button>
            </span>
          )}
        </div>
        {r.anonymous && <p className="muted small">{t('buying.offers.anonNote')}</p>}
        {waiting > 0 && <p className="callout callout-info small">{t('buying.offers.waitingAtPme', { n: waiting })}</p>}
        {offers && live.length === 0 && <p className="muted">{t('buying.offers.none')}</p>}
        {live.length > 0 && view === 'compare' && live.length > 1 && (
          <div className="offer-compare-wrap">
            <table className="offer-compare">
              <tbody>
                {rows.map(([k, cell, hi]) => (
                  <tr key={k}>
                    <th scope="row">{t(`buying.offers.${k}`)}</th>
                    {live.map((o) => <td key={o.id} className={hi && hi(o) ? 'best' : undefined}>{cell(o)}{hi && hi(o) && <><br /><span className="small">{t('buying.offers.best')}</span></>}</td>)}
                  </tr>
                ))}
                <tr><th scope="row" /> {live.map((o) => <td key={o.id}>{actions(o)}</td>)}</tr>
              </tbody>
            </table>
          </div>
        )}
        {live.length > 0 && (view === 'list' || live.length === 1) && (
          <div className="offer-cards">
            {live.map((o) => (
              <article key={o.id} className={`offer-card is-${o.status}`}>
                <div className="offer-card-head">{sellerCell(o)}<span className={`chip-status offer-status-${o.status}`}>{t(`buying.offerStatus.${o.status}`)}</span></div>
                <dl>
                  {rows.slice(1, 8).map(([k, cell]) => <div key={k}><dt>{t(`buying.offers.${k}`)}</dt><dd>{cell(o)}</dd></div>)}
                </dl>
                {o.message && <p className="offer-msg">{o.message}</p>}
                {(o.listing || o.document || o.files.length > 0) && <p className="small">{extras(o)}</p>}
                {actions(o)}
              </article>
            ))}
          </div>
        )}
      </section>

      {accepting && (
        <div className="req-modal-back" role="dialog" aria-modal="true" aria-labelledby="acc-h" onClick={(e) => { if (e.target === e.currentTarget) setAccepting(null); }}>
          <div className="req-modal">
            <h2 id="acc-h">{t('buying.offers.acceptTitle')}</h2>
            <p><strong>{accepting.seller?.companyName}</strong> — {money(accepting.price, accepting.currency, lang)} × {formatQty(accepting.quantity, accepting.unit, lang, t)}</p>
            <div style={{ display: 'grid', gap: 8 }}>
              <button type="button" className="btn btn-primary" onClick={() => setStatus(accepting, 'accepted', false)}>{t('buying.offers.acceptClose')}</button>
              <button type="button" className="btn btn-ghost" onClick={() => setStatus(accepting, 'accepted', true)}>{t('buying.offers.acceptKeep')}</button>
              <button type="button" className="btn btn-link" onClick={() => setAccepting(null)}>{t('buying.f.cancel')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
